"use client";

import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Dropdown from "./Dropdown";
import Image from "next/image";
import { useBookmarkCount } from "@/lib/bookmarks";

import type { NavbarLink, SubDrawer } from "../navbarData";

type Props = {
  links: NavbarLink[];
  subDrawers: Record<string, SubDrawer>;
};

export default function DesktopNavbar({ links, subDrawers }: Props) {
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const bookmarkCount = useBookmarkCount();

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (
        accountRef.current &&
        !accountRef.current.contains(e.target as Node)
      ) {
        setAccountOpen(false);
      }
    }

    if (accountOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [accountOpen]);

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  return (
    <div className="navbar__menu flex flex-row justify-end items-center xl:gap-3.5 gap-2 w-full h-auto mx-auto">
      <div className="flex xl:gap-3.5 gap-2">
        {links.map((link) => {
          if (link.subDrawerId) {
            return (
              <Dropdown key={link.label} link={link} subDrawers={subDrawers} />
            );
          }

          const active = isActive(link.href);

          return (
            <Link
              key={link.label}
              href={link.href}
              title={link.label}
              className={`navbar__menu-btn flex flex-row justify-center items-center px-4 py-2.5 gap-[0.6vw] rounded-[16px] font-lato font-bold text-[12px] leading-tight tracking-wide no-underline transition-all duration-150 whitespace-nowrap ${
                active
                  ? link.href === "/enseigner"
                    ? "bg-[#1B515E] text-white rounded-[30px]"
                    : link.href === "/rejoindre"
                      ? "bg-[#1D1D1D] text-white rounded-[30px]"
                      : "bg-[#1B365E] text-white rounded-[30px]"
                  : link.variant === "contact"
                    ? "bg-[#ffe4b4] text-[#1b365e] rounded-[28px] hover:bg-[#ffd48a] active:bg-[#d9d9d9] active:border active:border-[#40bfde] active:rounded-[24px] focus:bg-[#1B365E] focus:text-white focus:outline-none"
                    : "bg-white text-[#1b365e] hover:bg-[#f5f5f5] active:bg-[#d9d9d9] active:border active:border-[#40bfde] active:rounded-[24px] focus:bg-[#1B365E] focus:text-white focus:outline-none"
              } ${
                link.variant === "highlight" && !active
                  ? "hover:bg-[#c7f8ff] hover:rounded-[30px] focus:bg-[#1B365E] focus:text-white focus:rounded-[24px] focus:shadow-[0_2px_8px_rgba(27,54,94,0.08)]"
                  : ""
              } ${
                link.variant === "secondary" && !active
                  ? "bg-[#efefef] rounded-[28px] text-[#1b515e] hover:bg-[#c7f8ff] hover:rounded-[24px] hover:text-[#1b365e] active:bg-[#d9d9d9] active:border active:border-[#40bfde] focus:bg-[#1B365E] focus:text-white"
                  : ""
              }`}
            >
              {link.label}
            </Link>
          );
        })}
      </div>

      <div className="navbar__actions flex flex-row items-center xl:gap-3.5 gap-2 h-auto">
        <div className="navbar__divider w-0.5 h-6.75 bg-[#c7f8ff] opacity-60 rounded-xs"></div>

        {/* {user ? (
          <div ref={accountRef} className="relative inline-block">
            <button
              className={`navbar__account-btn relative min-w-[80px] min-h-[38px] bg-white border border-transparent rounded-[84px] cursor-pointer p-2 hover:bg-[#ffe4b4] active:bg-[#fc7f16] ${
                accountOpen
                  ? 'is-open bg-[#ffe4b4] font-bold border-t-[20px] border-t-[#ffe4b4] border-b-0 border-l-0 border-r-0 rounded-t-[20px] rounded-b-none z-[100001] after:content-[""] after:absolute after:bottom-[-8px] after:left-0 after:right-0 after:h-[8px] after:bg-[#ffe4b4]'
                  : ""
              }`}
              style={{
                backgroundImage: accountOpen
                  ? "linear-gradient(#ffe4b4, #ffe4b4), linear-gradient(180deg, #ffbc41 26%, #ff5758 100%)"
                  : "linear-gradient(white, white), linear-gradient(180deg, #ffbc41 26%, #ff5758 100%)",
                backgroundOrigin: "border-box",
                backgroundClip: "padding-box, border-box",
              }}
              aria-label="Mon compte"
              aria-expanded={accountOpen}
              onClick={() => setAccountOpen((v) => !v)}
            >
              <div
                className={`navbar__account-icon absolute left-[21%] transition-transform -translate-y-1/2 ${
                  accountOpen ? "-top-0.5" : "top-1/2"
                } flex items-center justify-center transition-all duration-150`}
              >
                <Image
                  className="w-4.75 h-4.05"
                  src="/assets/icons/icon-monCompte.svg"
                  alt="Mon compte"
                  title="Mon compte"
                  width={25}
                  height={25}
                />
              </div>
              <div
                className={`navbar__arrow absolute left-[60.25%] transition-transform -translate-y-1/2 ${
                  accountOpen ? "top-[-1.5px]" : "top-1/2"
                } flex items-center justify-center transition-all duration-150 ${
                  accountOpen ? "rotate-180" : ""
                }`}
              >
                <Image
                  src="/assets/icons/down-arrow.svg"
                  alt="Ouvrir le menu"
                  title="Ouvrir le menu"
                  width={16}
                  height={16}
                  className="w-4 h-auto min-w-4 min-h-3"
                />
              </div>
            </button>

            {accountOpen && (
              <div className="absolute -right-12.5 top-[calc(100%+5px)] z-100020 w-115 h-fit overflow-hidden">
                
                <div
                  className="w-full rounded-3xl p-[1px]"
                  style={{
                    background:
                      "linear-gradient(180deg, #FFBC41 0%, #FF5758 100%)",
                  }}
                >
                  <div className="relative w-full rounded-3xl bg-[#FFE4B4] overflow-hidden">
                    <div className="login-responsive absolute right-6 top-[23px] xl:inline-flex items-center justify-center w-[27px] h-[15px]">
                      <Image
                        src="/assets/small-logo.svg"
                        alt="small-logo"
                        title="small-logo"
                        width={30}
                        height={17}
                      />
                    </div>
                    <WelcomeDashboard
                      firstName={user.firstName}
                      onLogout={() => {
                        logout();
                        setAccountOpen(false);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <Link
            href="/login"
            title="Mon compte"
            className="navbar__account-btn relative inline-block min-w-20 min-h-9.5 bg-white border border-transparent rounded-[84px] cursor-pointer p-2 no-underline hover:bg-[#ffe4b4] active:bg-[#fc7f16]"
            style={{
              backgroundImage:
                pathname === "/login"
                  ? "linear-gradient(#ffe4b4, #ffe4b4), linear-gradient(180deg, #ffbc41 26%, #ff5758 100%)"
                  : "linear-gradient(white, white), linear-gradient(180deg, #ffbc41 26%, #ff5758 100%)",
              backgroundOrigin: "border-box",
              backgroundClip: "padding-box, border-box",
            }}
            aria-label="Mon compte"
            aria-current={pathname === "/login" ? "page" : undefined}
          >
            <div className="navbar__account-icon absolute left-[21%] top-1/2 -translate-y-1/2 flex items-center justify-center">
              <Image
                className="w-[19px] min-w-[19px] h-[16.19px] min-h-[16.19px]"
                src="/assets/icons/icon-monCompte.svg"
                alt="Mon compte"
                title="Mon compte"
                width={25}
                height={25}
              />
            </div>
            <div
              className="navbar__arrow absolute left-[60.25%] top-1/2 -translate-y-1/2 flex items-center justify-center"
              aria-hidden="true"
            >
              <Image
                src="/assets/icons/down-arrow.svg"
                alt=""
                width={16}
                height={16}
                className="w-4 h-auto min-w-4 min-h-3"
              />
            </div>
          </Link>
        )} */}

        <Link
          href="/favoris"
          title="Favoris"
          aria-label={`Favoris (${bookmarkCount})`}
          className="navbar__bookmark-btn relative flex flex-row justify-center items-center p-2.5 w-10 min-w-10 h-10 min-h-10 bg-white border border-[#fc7f16] rounded-full cursor-pointer transition-all hover:bg-[#fff5e6] hover:scale-105"
        >
          {bookmarkCount > 0 ? (
            <div className="relative w-11 h-11 min-h-8.5">
              <svg
                viewBox="0 0 24 24"
                className="w-full h-full"
                fill="#FC7F16"
                aria-hidden="true"
              >
                <path d="M6 2a2 2 0 0 0-2 2v18l8-5.5L20 22V4a2 2 0 0 0-2-2H6z" />
              </svg>
              <span
                className="absolute inset-0 mt-3.5 flex items-start justify-center text-white text-[10px] leading-none font-bold"
                aria-hidden="true"
              >
                {bookmarkCount > 9 ? "9+" : bookmarkCount}
              </span>
            </div>
          ) : (
            <Image
              className="w-5 h-5 min-h-4.5"
              src="/assets/icons/icon-bookmark.svg"
              alt="Favoris"
              title="Favoris"
              width={25}
              height={25}
            />
          )}
        </Link>
      </div>
    </div>
  );
}
